import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './components/AdminLayout';
import { API_ENDPOINTS } from '../config/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    active_orders: 0,
    completed_orders: 0,
    pending_deliveries: 0,
    total_revenue: 0,
    total_customers: 0,
    recent_orders: 0,
    monthly_revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching dashboard stats from:', API_ENDPOINTS.ADMIN.DASHBOARD_STATS);
      
      const response = await axios.get(API_ENDPOINTS.ADMIN.DASHBOARD_STATS, {
        timeout: 8000,
      });
      
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApiError = (error) => {
    if (error.code === 'ECONNABORTED') {
      setError('Request timeout while fetching dashboard data.');
    } else if (error.response) {
      setError(`Server error: ${error.response.status}. Unable to load dashboard.`);
    } else if (error.request) {
      setError('Network error. Please check if the server is running.');
    } else {
      setError('Unable to load dashboard data. Please try again later.');
    }
  };

  const handleRetry = () => {
    fetchDashboardStats();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <AdminLayout>
      <div className="admin-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Dashboard Overview</h2>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={fetchDashboardStats}
            disabled={loading}
          >
            <i className={`fas fa-refresh ${loading ? 'fa-spin' : ''} me-2`}></i>
            Refresh
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-warning alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <div className="mt-2">
              <button className="btn btn-primary btn-sm" onClick={handleRetry}>
                <i className="fas fa-redo me-2"></i>
                Try Again
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading dashboard data...</p>
          </div>
        ) : (
          <>
            <div className="row">
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stat-card border-left-primary shadow h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                          Active Orders
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {stats.active_orders}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-clipboard-list fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stat-card border-left-success shadow h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                          Completed Orders
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {stats.completed_orders}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stat-card border-left-warning shadow h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                          Pending Deliveries
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {stats.pending_deliveries}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-truck fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stat-card border-left-info shadow h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                          Total Revenue
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {formatCurrency(stats.total_revenue)}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-rupee-sign fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Stats Row */}
            <div className="row">
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stat-card border-left-secondary shadow h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-secondary text-uppercase mb-1">
                          Total Customers
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {stats.total_customers}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-users fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stat-card border-left-primary shadow h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                          Recent Orders (7 days)
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {stats.recent_orders}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-shopping-cart fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stat-card border-left-success shadow h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                          Monthly Revenue
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {formatCurrency(stats.monthly_revenue)}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-chart-line fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stat-card border-left-warning shadow h-100 py-2">
                  <div className="card-body">
                    <div className="row no-gutters align-items-center">
                      <div className="col mr-2">
                        <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                          Balance Orders
                        </div>
                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                          {stats.active_orders - stats.pending_deliveries}
                        </div>
                      </div>
                      <div className="col-auto">
                        <i className="fas fa-clock fa-2x text-gray-300"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Navigation */}
            <div className="row mt-4">
              <div className="col-12">
                <h5 className="mb-3">Quick Navigation</h5>
              </div>
              <div className="col-md-2 col-sm-4 mb-3">
                <Link to="/admin/customers" className="btn btn-primary w-100 h-100 py-3 d-flex flex-column align-items-center">
                  <i className="fas fa-users fa-2x mb-2"></i>
                  <span>Customers</span>
                </Link>
              </div>
              <div className="col-md-2 col-sm-4 mb-3">
                <Link to="/admin/measurements" className="btn btn-primary w-100 h-100 py-3 d-flex flex-column align-items-center">
                  <i className="fas fa-ruler fa-2x mb-2"></i>
                  <span>Measurements</span>
                </Link>
              </div>
              <div className="col-md-2 col-sm-4 mb-3">
                <Link to="/admin/orders" className="btn btn-primary w-100 h-100 py-3 d-flex flex-column align-items-center">
                  <i className="fas fa-shopping-cart fa-2x mb-2"></i>
                  <span>Orders</span>
                </Link>
              </div>
              <div className="col-md-2 col-sm-4 mb-3">
                <Link to="/admin/services" className="btn btn-primary w-100 h-100 py-3 d-flex flex-column align-items-center">
                  <i className="fas fa-concierge-bell fa-2x mb-2"></i>
                  <span>Services</span>
                </Link>
              </div>
              <div className="col-md-2 col-sm-4 mb-3">
                <Link to="/admin/gallery" className="btn btn-primary w-100 h-100 py-3 d-flex flex-column align-items-center">
                  <i className="fas fa-images fa-2x mb-2"></i>
                  <span>Gallery</span>
                </Link>
              </div>
              <div className="col-md-2 col-sm-4 mb-3">
                <Link to="/admin/reports" className="btn btn-primary w-100 h-100 py-3 d-flex flex-column align-items-center">
                  <i className="fas fa-chart-bar fa-2x mb-2"></i>
                  <span>Reports</span>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        .stat-card {
          border-left: 0.25rem solid !important;
        }
        .border-left-primary { border-left-color: #4e73df !important; }
        .border-left-success { border-left-color: #1cc88a !important; }
        .border-left-info { border-left-color: #36b9cc !important; }
        .border-left-warning { border-left-color: #f6c23e !important; }
        .border-left-secondary { border-left-color: #858796 !important; }
      `}</style>
    </AdminLayout>
  );
};

export default Dashboard;